import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaPen,
  FaSyncAlt,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebSegments,
  createWhatsAppWebSegment,
  updateWhatsAppWebSegment,
  deleteWhatsAppWebSegment,
  validateWhatsAppWebSegment,
  getWhatsAppWebSegment,
} from "../../../../services/api";
import SegmentWizard from "./SegmentWizard";

export default function SegmentsPage() {
  const { user } = useAuth();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | { id, ... }
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(null);
  const [validateResult, setValidateResult] = useState(null);
  const [details, setDetails] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppWebSegments();
      setSegments(res?.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  async function handleSubmit(payload) {
    setSaving(true);
    try {
      if (editing && editing !== "new" && editing.id) {
        await updateWhatsAppWebSegment(editing.id, payload);
      } else {
        await createWhatsAppWebSegment(payload);
      }
      setEditing(null);
      await reload();
    } catch (err) {
      alert("Falha ao salvar segmento.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(seg) {
    if (!window.confirm(`Excluir o segmento "${seg.name}"?`)) return;
    await deleteWhatsAppWebSegment(seg.id);
    reload();
  }

  async function handleValidate(seg) {
    setValidating(seg.id);
    try {
      const res = await validateWhatsAppWebSegment(seg.id);
      setValidateResult({ segment: seg, ...res?.data });
    } finally {
      setValidating(null);
    }
  }

  async function handleViewDetails(seg) {
    const res = await getWhatsAppWebSegment(seg.id);
    setDetails(res?.data);
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          {editing ? (
            <>
              <h2 className="mb-3">
                {editing === "new" ? "Novo Segmento" : "Editar Segmento"}
              </h2>
              <SegmentWizard
                initial={
                  editing === "new"
                    ? null
                    : {
                        name: editing.name,
                        description: editing.description,
                        channelIds: editing.channels?.map((c) => c.id) || [],
                        groupJids: editing.group_jids || [],
                      }
                }
                onCancel={() => setEditing(null)}
                onSubmit={handleSubmit}
                saving={saving}
              />
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h2 className="mb-1">Segmentos</h2>
                  <p className="text-muted mb-0">
                    Organize seus canais e grupos de WhatsApp
                  </p>
                </div>
                <Button variant="primary" onClick={() => setEditing("new")}>
                  <FaPlus /> Novo Segmento
                </Button>
              </div>

              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" />
                </div>
              ) : segments.length === 0 ? (
                <Card className="text-center p-5">
                  <Card.Body>
                    <p>Nenhum segmento cadastrado.</p>
                    <Button variant="primary" onClick={() => setEditing("new")}>
                      <FaPlus /> Criar Primeiro Segmento
                    </Button>
                  </Card.Body>
                </Card>
              ) : (
                <Row>
                  {segments.map((s) => (
                    <Col key={s.id} md={6} lg={4} className="mb-3">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="mb-1">{s.name}</h5>
                            <Badge variant="success">● Ativo</Badge>
                          </div>
                          {s.description && (
                            <small className="text-muted d-block">
                              {s.description}
                            </small>
                          )}
                          <hr />
                          <div className="d-flex justify-content-between mb-2">
                            <small>
                              🌐 <strong>{s.total_channels}</strong> canais
                            </small>
                            <small>
                              👥 <strong>{s.total_groups}</strong> grupos
                            </small>
                          </div>
                          <div
                            className="d-flex flex-wrap"
                            style={{ gap: 6 }}
                          >
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => handleViewDetails(s)}
                            >
                              <FaEye /> Ver
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setEditing(s)}
                            >
                              <FaPen /> Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-info"
                              disabled={validating === s.id}
                              onClick={() => handleValidate(s)}
                            >
                              <FaSyncAlt />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(s)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Modal de validação */}
      <Modal
        show={!!validateResult}
        onHide={() => setValidateResult(null)}
        centered
      >
        <Modal.Body className="text-center p-4">
          {validateResult?.healthy ? (
            <>
              <div style={{ fontSize: 48, color: "#28a745" }}>
                <FaCheckCircle />
              </div>
              <h5 className="text-success">Tudo Certo!</h5>
              <p>Todos os grupos estão acessíveis.</p>
              <Card
                className="my-3"
                style={{ background: "rgba(40,167,69,0.15)", border: 0 }}
              >
                <Card.Body className="py-2">
                  <small>
                    ✅ Nenhum grupo foi removido ou se tornou inacessível. O
                    segmento está 100% válido!
                  </small>
                </Card.Body>
              </Card>
            </>
          ) : (
            <>
              <div style={{ fontSize: 48 }}>⚠️</div>
              <h5 className="text-warning">Atenção</h5>
              <p>
                {validateResult?.missing?.length} grupo(s) não estão mais
                acessíveis.
              </p>
            </>
          )}
          <Button variant="success" onClick={() => setValidateResult(null)}>
            Entendido
          </Button>
        </Modal.Body>
      </Modal>

      {/* Modal de detalhes */}
      <Modal show={!!details} onHide={() => setDetails(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>📦 Detalhes do Segmento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {details && (
            <>
              <Row className="mb-3">
                <Col>
                  <strong>Nome:</strong> {details.name}
                </Col>
                <Col>
                  <strong>Canais:</strong> {details.total_channels}
                </Col>
                <Col>
                  <strong>Grupos:</strong> {details.total_groups}
                </Col>
              </Row>
              <h6>Canais e Grupos</h6>
              {details.channels?.map((ch) => (
                <Card key={ch.id} className="mb-2">
                  <Card.Body>
                    <strong>{ch.name}</strong>{" "}
                    <Badge
                      variant={ch.status === "active" ? "success" : "secondary"}
                    >
                      {ch.status}
                    </Badge>
                    <ul className="mb-0 mt-2">
                      {ch.groups?.map((g) => (
                        <li key={g.group_jid}>{g.group_name}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              ))}
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}
